// Per-entry intent labels for a queued-edits proposal Bundle: the proposer
// (AI/backend) records one short plain-English phrase per entry describing WHY
// it changes, stored ON the Bundle so it is self-contained — a page refresh
// rebuilds the review (and its intent labels) from the persisted Bundle alone.
// The review card renders this in place of the raw "N changes" count: the field
// count is not meaningful to reviewers, the intent is.
//
// ##EntryHandle Intents live on `Bundle.entry[*].extension` as a single
// `valueString` extension per entry, keyed by the entry it sits on.

import type { Bundle, BundleEntry } from '@medplum/fhirtypes'
import { QUEUED_EDITS_ENTRY_INTENT_EXTENSION } from '#src/codeSystems.ts'
import { entryHandle, matchesHandle } from '#src/queued-edits/entry-handle.ts'

// Record one proposer intent label for a single entry. Idempotent per entry —
// re-setting replaces the entry's prior intent extension. Pass undefined to
// clear it. Mutates and returns the bundle.
export function setEntryIntent(
  bundle: Bundle,
  entry: string,
  intent: string | undefined
): Bundle {
  const target = (bundle.entry ?? []).find((e) => matchesHandle(e, entry))
  if (!target) {
    return bundle
  }
  const others = (target.extension ?? []).filter(
    (e) => e.url !== QUEUED_EDITS_ENTRY_INTENT_EXTENSION
  )
  if (intent) {
    target.extension = [
      ...others,
      { url: QUEUED_EDITS_ENTRY_INTENT_EXTENSION, valueString: intent },
    ]
  } else if (others.length > 0) {
    target.extension = others
  } else {
    delete target.extension
  }
  return bundle
}

// Record several intent labels at once, keyed by entry handle (the same handle
// `getEntryIntents` returns). Entries with no matching handle are ignored.
// Mutates and returns the bundle.
export function setEntryIntents(
  bundle: Bundle,
  intents: Record<string, string>
): Bundle {
  for (const [handle, intent] of Object.entries(intents)) {
    setEntryIntent(bundle, handle, intent)
  }
  return bundle
}

// The intent label recorded for an entry, if any.
export function getEntryIntent(entry: BundleEntry): string | undefined {
  return entry.extension?.find(
    (e) => e.url === QUEUED_EDITS_ENTRY_INTENT_EXTENSION
  )?.valueString
}

interface EntryIntents {
  [handle: string]: string
}

// All recorded intent labels keyed by entry handle.
export function getEntryIntents(bundle: Bundle): EntryIntents {
  const intents: EntryIntents = {}
  for (const entry of bundle.entry ?? []) {
    const intent = getEntryIntent(entry)
    const handle = entryHandle(entry)
    if (intent && handle) {
      intents[handle] = intent
    }
  }
  return intents
}
